import { ViewPermissions } from '@/components/Auth/ViewPermissions';
import { ViewPaths, ViewConfig } from '@/types/views';
import {
  getDefaultView,
  getViewsForType,
  hasViewPermission,
  isValidView,
} from '@/utils/viewHelpers';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { DataNotFound } from '@/components/DataNotFound';
import Header from '@/components/Header/Header';
import { useIntl, FormattedMessage } from 'react-intl';
import { HeaderMenu } from '@/components/Header/HeaderMenu';
import { DepartmentAdmins } from '@/components/Departments/DepartmentAdmins';
import { DepartmentPayerForm } from '@/components/Departments/DepartmentPayerForm';
import { CenteredColumn } from '@/components/CenteredColumn';
import { Section } from '@/components/Section';
import { ActionButtons } from '@/components/Header/ActionButtons';
import Button from '@/components/core/Button';
import { IconUserPlus, IconDataImport } from '@/icons';
import { StudentList } from '@/components/Students/StudentList';
import { CollectionImportList } from '@/components/Import/CollectionImportList';
import { DialogWindow } from '@/components/Dialog';
import { StudentProfileForm } from '@/components/Students/StudentProfileForm';
import { SearchFilterParams, SortDirection } from '@/types/filterSearch';
import { DocumentHead } from '@/components/core/DocumentHead';
import { useGetStudentCollection } from '@/hooks/useGetStudentCollection';
import { StudentCategoryPeriods } from '@/components/Organizations/StudentCategoryPeriods';
import { Loading } from '@/components/Loading';
import { useCollectionClaimWarning } from '@/hooks/useCollectionClaimWarning';
import { useStudentCategoryEmpty } from '@/hooks/useStudentCategoryEmpty';
import { StudentCategoryInfo } from '@/components/Students/StudentCategoryInfo';
import type { CollectionTypeDTO } from '@/api/trafikportalen/models/collectionTypeDTO';

export const Route = createFileRoute(
  '/$tenantId/_layout/students/travels/$view',
)({
  component: StudentTravelView,
  validateSearch: (search: Record<string, unknown>): SearchFilterParams => {
    return {
      query: search.query as string,
      status: search.status as string[],
      halfProduct: search.halfProduct as string,
      sort: search.sort as string,
      direction: search.direction as SortDirection,
      manuallyModified: search.manuallyModified as string[],
      claimState: search.claimState as string[],
    };
  },
  beforeLoad: ({ params: { tenantId, view } }) => {
    if (
      !isValidView(view, 'students/travels') ||
      !hasViewPermission(view, 'students/travels', 'organization:viewer')
    ) {
      throw redirect({
        to: '/$tenantId/students/travels/$view',
        params: {
          tenantId,
          view: getDefaultView('students/travels'),
        },
      });
    }
  },
});

function StudentTravelView() {
  const { tenantId, view } = Route.useParams();
  const selected = view as ViewPaths<'students/travels'>;
  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  const { students, collectionId, collection, department, isLoading } =
    useGetStudentCollection('travels');
  const { hasWarning: hasClaimWarning } =
    useCollectionClaimWarning(collectionId);
  const isCategoryEmpty = useStudentCategoryEmpty(collectionId, students);

  const [open, setOpen] = useState<boolean>(false);

  const onViewChange = useCallback(
    (view?: string) => {
      if (!view) return;

      navigate({
        to: Route.to,
        params: { tenantId, view },
        replace: false,
      });
    },
    [navigate, tenantId],
  );

  const onChange = () => {
    setOpen(true);
  };

  const views = useMemo(() => {
    return getViewsForType('students/travels') as ViewConfig[];
  }, []);

  const metadataKeys = useMemo(() => {
    switch (selected) {
      case 'list':
        return {
          titleKey: 'page.students.travels.list.title',
          descriptionKey: 'page.students.travels.list.description',
        };
      case 'admins':
        return {
          titleKey: 'page.administrators.title',
          descriptionKey: 'page.administrators.description',
        };
      case 'invoicing':
        return {
          titleKey: 'page.students.invoicing.title',
          descriptionKey: 'page.students.invoicing.description',
        };
      case 'periods':
        return {
          titleKey: 'page.students.travels.periods.title',
          descriptionKey: 'page.students.travels.periods.description',
        };
      case 'imports':
        return {
          titleKey: 'page.imports.title',
          descriptionKey: 'page.imports.description',
        };
      case 'info':
        return {
          titleKey: 'page.students.travels.info.title',
          descriptionKey: 'page.students.travels.info.description',
        };
      default:
        return {
          titleKey: 'page.students.travels.title',
          descriptionKey: 'page.students.travels.description',
        };
    }
  }, [selected]);

  if (isLoading) {
    return <Loading />;
  }

  if (!students) {
    return (
      <DataNotFound
        description={
          <FormattedMessage defaultMessage="Kan inte hämta lista på elever" />
        }
        buttonTitle={<FormattedMessage defaultMessage="Till start" />}
        onClick={() => navigate({ to: '/$tenantId', params: { tenantId } })}
      />
    );
  }

  return (
    <ViewPermissions permission="organization:viewer">
      <div className="flex grow flex-col overflow-y-auto">
        <DocumentHead
          titleKey={metadataKeys.titleKey}
          descriptionKey={metadataKeys.descriptionKey}
        />
        <Header
          title={formatMessage({ defaultMessage: 'Elevresor' })}
          menu={
            <HeaderMenu
              options={views}
              selected={selected}
              onViewChange={onViewChange}
              notificationViews={[
                ...(hasClaimWarning ? ['periods'] : []),
                ...(isCategoryEmpty ? ['info'] : []),
              ]}
              notificationLabels={{
                periods: formatMessage({
                  defaultMessage: 'Ingen aktiv period',
                }),
                info: formatMessage({
                  defaultMessage: 'Kategorin saknar personer',
                }),
              }}
            />
          }
          actions={
            <ViewPermissions
              permission="organization:editor"
              departmentId={department?.id}
            >
              <ActionButtons>
                <Button
                  startIcon={<IconUserPlus />}
                  onClick={onChange}
                  variant="outline"
                >
                  <FormattedMessage defaultMessage="Ny elev" />
                </Button>
                <Button
                  startIcon={<IconDataImport />}
                  onClick={() => {
                    navigate({
                      to: '/$tenantId/students/travels/import',
                      params: { tenantId },
                    });
                  }}
                  variant="outline"
                >
                  <FormattedMessage defaultMessage="Importera" />
                </Button>
              </ActionButtons>
            </ViewPermissions>
          }
        />
        {selected === 'admins' && department && (
          <div className="w-full px-4 md:px-8">
            <DepartmentAdmins departmentId={department.id} />
          </div>
        )}
        {selected === 'invoicing' && department && (
          <CenteredColumn>
            <Section>
              <DepartmentPayerForm departmentId={department.id} />
            </Section>
          </CenteredColumn>
        )}
        {selected === 'list' && (
          <StudentList
            students={students}
            routeType={Route}
            studentType="travels"
            collectionId={collectionId ?? undefined}
          />
        )}
        {selected === 'periods' && collectionId && (
          <CenteredColumn>
            <Section>
              <StudentCategoryPeriods
                collectionId={collectionId}
                collectionType={collection?.type as CollectionTypeDTO}
              />
            </Section>
          </CenteredColumn>
        )}
        {selected === 'imports' && collectionId && (
          <CollectionImportList
            collectionId={collectionId}
            studentType="travels"
            tenantId={tenantId}
          />
        )}
        {selected === 'info' && (
          <CenteredColumn className="grow">
            <StudentCategoryInfo studentType="travels" />
          </CenteredColumn>
        )}
        <DialogWindow
          title={formatMessage({ defaultMessage: 'Lägg till ny elev' })}
          content={
            <StudentProfileForm
              onOpenChange={setOpen}
              type="travels"
              collectionId={collectionId ?? undefined}
              departmentId={department?.id}
            />
          }
          onOpenChange={setOpen}
          open={open}
        />
      </div>
    </ViewPermissions>
  );
}
